/**
 * Cost of Goods save-time validation (BLU-599).
 *
 * Extracted from `CostOfGoods.tsx` so the rule set is unit-testable and so the
 * one subtlety below is documented in one place.
 *
 * A supplier-less row that carries a cost is only a real error when the operator
 * actually *entered* that cost — because {@link buildSuppliers} drops supplier-less
 * rows, so a typed-but-supplier-less cost would be silently lost on save.
 *
 * BUT the page seeds a supplier-less rank-1 row carrying the category's existing
 * `landed_cost` for every category that has a cost but no per-category supplier yet
 * — which, until BLU-599's suppliers are populated, is essentially *every* costed
 * category. Those seeded rows are the untouched pre-existing state: they are not
 * part of the changed-row set that actually gets saved, and their cost is dropped
 * by `buildSuppliers` anyway. Blocking the whole save on them (as the first cut of
 * BLU-599 did) bricks Save for existing tenants and even names a category the
 * operator never touched. So we only block when the supplier-less cost differs from
 * the seeded initial value (matched by stable `uid`) — i.e. the operator entered or
 * edited it. Editing an unrelated field on the same category (e.g. factory cost)
 * leaves the seeded supplier row's `uid`/`unitCost` untouched, so it still won't
 * block.
 */

export type ValidationSupplier = {
  uid: string;
  supplierId: string | null;
  unitCost: string;
};

export type ValidationRow = {
  productType: string;
  suppliers: ValidationSupplier[];
};

export type SaveBlocker = {
  productType: string;
  /**
   * `costWithoutSupplier` — a cost the operator entered on a row with no supplier picked.
   * `invalidSupplierCost` — a picked supplier whose cost is missing, non-numeric, or negative.
   * Both surface the same `landedCostInvalid` toast today; the distinction aids testing/future copy.
   */
  reason: 'costWithoutSupplier' | 'invalidSupplierCost';
};

/**
 * Returns the first row that must block the save, or `null` if every row is valid.
 * `rows` is the current editor state; `initialRows` is the seeded load-time state
 * (same array order — rows are per-category and fixed; suppliers match by `uid`).
 */
export function findCostOfGoodsSaveBlocker(
  rows: ValidationRow[],
  initialRows: ValidationRow[],
): SaveBlocker | null {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const init = initialRows[i];
    for (const s of row.suppliers) {
      const costEntered = s.unitCost.trim() !== '';
      if (!s.supplierId) {
        if (!costEntered) continue;
        // Untouched seeded legacy cost (no supplier assigned yet) — not saved, must not block.
        const seeded = init?.suppliers.find((x) => x.uid === s.uid);
        const untouchedLegacyCost =
          seeded != null && !seeded.supplierId && seeded.unitCost === s.unitCost;
        if (untouchedLegacyCost) continue;
        return { productType: row.productType, reason: 'costWithoutSupplier' };
      }
      const n = Number(s.unitCost);
      if (!costEntered || Number.isNaN(n) || n < 0) {
        return { productType: row.productType, reason: 'invalidSupplierCost' };
      }
    }
  }
  return null;
}
